import { useState, useEffect, createContext, FC, useMemo } from 'react';
import language, { TLanguage } from '../util/language';
import API, { EGatewayAPIBaseUrl } from '../api/api';
import { Theme, ThemeProvider } from '@mui/material/styles';
import {
    EAppEnvironment,
    EQueryStringParam,
    ERoutes,
    EConnectBaseUrl,
    EWebClientsBaseUrl,
    EAdminBaseUrl,
    ERestAPIBaseUrl,
    EStaffAccountType,
    EPromiseResolution,
    loginLinkLocalStorageKey,
    EIdentityClientId,
    ELDFlags,
    EMixpanelEnvId,
    EMixpanelAppName,
    EUserType,
    EBrowserOS,
    mixpanelEntryPointWhitelist,
} from '../util/constants';
import { TLoginInfoResponse, TSiteInfoResponse } from '../api/types';
import { loginIsWithinTimeWindow } from '../util/helpers';
import { MindbodyRebrand, MindbodyBusiness } from '@mindbody/component-library';
import { colord } from 'colord';
import mixpanel from 'mixpanel-browser';
import { useFlags, useLDClient, LDSingleKindContext } from 'launchdarkly-react-client-sdk';
import Bowser from 'bowser';
import { Redirect, useLocation } from 'react-router-dom';

export type TCoBranding = {
    name: string;
    url: string;
    logoUrl: string;
    brandColor: string;
    isDarkMode: boolean;
    isDefaultFamilyAccount: boolean;
};

export type TLoginLinkLocalStorage = {
    loginLink: string;
    timeSetEpoch: number;
};

export type TCountry = { name: string; code: string };

export type TGlobalProviderProps = {
    value?: TGlobalContext;
};

export type TBrowser = {
    OS: EBrowserOS;
};

type TEntryPointUrl = {
    path: ERoutes;
    isAtEntryPoint: boolean;
};

export type TGlobalContext = {
    language: TLanguage;
    browser: TBrowser;
    csrfToken: string;
    getCsrfToken: () => Promise<void>;
    putFullLoginLinkInLocalStorage: (loginHint?: string) => void;
    getFullLoginLinkFromLocalStorage: () => void;
    removeFullLoginLinkFromLocalStorage: () => void;
    staffAccountType: EStaffAccountType;
    setStaffAccountType: (type: EStaffAccountType) => void;
    countriesList: TCountry[];
    getCountriesList: () => Promise<void>;
    gatewayAPIBaseUrl: EGatewayAPIBaseUrl;
    restAPIBaseUrl: ERestAPIBaseUrl;
    connectBaseUrl: EConnectBaseUrl;
    webClientsBaseUrl: EWebClientsBaseUrl;
    adminBaseUrl: EAdminBaseUrl;
    identityClientId: EIdentityClientId;
    setIdentityClientId: (clientId: EIdentityClientId) => void;
    subscriberId: string;
    setSubscriberId: (subscriberId: string) => void;
    customerAccountId: string;
    setCustomerAccountId: (id: string) => void;
    isStaffLogin: boolean;
    setIsStaffLogin: (isStaffLogin: boolean) => void;
    getCoBranding: () => Promise<void>;
    coBranding: TCoBranding;
    setCoBranding: (coBranding: TCoBranding) => void;
    coBrandingChecked: boolean;
    returnUrlParameter: string;
    setReturnUrlParameter: (returnUrl: string) => void;
    fullLoginLink: string;
    setFullLoginLink: (loginLink: string) => void;
    consumerLoginLink: string;
    setConsumerLoginLink: (loginUrl: string) => void;
    staffLoginLink: string;
    setStaffLoginLink: (loginUrl: string) => void;
    appUiState: EAppUiState;
    setAppUiState: (appUiState: EAppUiState) => void;
    updateLaunchDarklyContext: (userId: string) => Promise<void>;
    setMixpanelSurface: (surface: EMixpanelAppName) => void;
    entryPointUrl: TEntryPointUrl;
};

export enum EAppUiState {
    default,
    initialized,
    ready,
    error,
}

export const defaultCoBranding: TCoBranding = {
    name: '',
    url: '',
    logoUrl: '',
    brandColor: '',
    isDarkMode: false,
    isDefaultFamilyAccount: false,
};

const GlobalContext = createContext<TGlobalContext | undefined>(undefined);

export const GlobalProvider: FC<TGlobalProviderProps> = (props) => {
    const [csrfToken, setCsrfToken] = useState('');
    const [staffAccountType, setStaffAccountType] = useState(EStaffAccountType.subscriber);
    const [countriesList, setCountriesList] = useState<TCountry[]>([]);
    const [appUiState, setAppUiState] = useState(EAppUiState.default);
    const [gatewayAPIBaseUrl, setGatewayAPIBaseUrl] = useState(EGatewayAPIBaseUrl.production);
    const [restAPIBaseUrl, setRestAPIBaseUrl] = useState(ERestAPIBaseUrl.production);
    const [connectBaseUrl, setConnectBaseUrl] = useState(EConnectBaseUrl.production);
    const [webClientsBaseUrl, setWebClientsBaseUrl] = useState(EWebClientsBaseUrl.production);
    const [adminBaseUrl, setAdminBaseUrl] = useState(EAdminBaseUrl.production);
    const [mixPanelEnvId, setMixpanelEnvId] = useState(EMixpanelEnvId.production);
    const [identityClientId, setIdentityClientId] = useState<EIdentityClientId | null>(null);
    const [subscriberId, setSubscriberId] = useState('');
    const [customerAccountId, setCustomerAccountId] = useState('');
    const [returnUrlParameter, setReturnUrlParameter] = useState('');
    const [fullLoginLink, setFullLoginLink] = useState('');
    const [consumerLoginLink, setConsumerLoginLink] = useState('');
    const [staffLoginLink, setStaffLoginLink] = useState('');
    const [coBranding, setCoBranding] = useState<TCoBranding>(defaultCoBranding);
    const [coBrandingChecked, setCoBrandingChecked] = useState(false);
    const [isStaffLogin, setIsStaffLogin] = useState(false);
    const [isMixpanelSetup, setIsMixpanelSetup] = useState(false);
    const [mixpanelSurface, setMixpanelSurface] = useState<EMixpanelAppName | null>(null);
    const [entryPointUrl, setEntryPointUrl] = useState<TEntryPointUrl | null>(null);
    const [theme, setTheme] = useState<Theme>(MindbodyRebrand);
    const location = useLocation();
    const ldClient = useLDClient();
    const {
        [ELDFlags.useConsumerPasscodeVerification]: isPasscodeVerificationEnabled,
        [ELDFlags.newConsumerIdentity]: isNewConsumerIdentityEnabled,
    } = useFlags();
    const browser: TBrowser = useMemo(() => {
        const parsedBrowser = Bowser.getParser(window.navigator.userAgent);
        return {
            OS: parsedBrowser.getOSName() as EBrowserOS,
        };
    }, []);

    // ----------------
    // Get countries
    // ----------------
    const getCountriesList = async () => {
        try {
            const response = await API.get.countriesList(gatewayAPIBaseUrl);
            const countriesList = response.data;
            setCountriesList(countriesList);
        } catch (error) {
            setAppUiState(EAppUiState.error);
            console.error('Failed to get countries list');
        }
    };

    // ------------
    // Full login link local storage
    // ------------
    const putFullLoginLinkInLocalStorage = (loginHint?: string) => {
        if (fullLoginLink) {
            const fullLoginLinkToUse = loginHint
                ? `${fullLoginLink}&${EQueryStringParam.loginHint}=${loginHint}`
                : fullLoginLink;
            const payload: TLoginLinkLocalStorage = {
                loginLink: fullLoginLinkToUse,
                timeSetEpoch: Date.now(),
            };
            localStorage.setItem(loginLinkLocalStorageKey, JSON.stringify(payload));
        }
    };

    const getFullLoginLinkFromLocalStorage = () => {
        const loginLinkLocalStorageDetails: TLoginLinkLocalStorage = JSON.parse(
            localStorage.getItem(loginLinkLocalStorageKey),
        );
        if (loginIsWithinTimeWindow(loginLinkLocalStorageDetails?.timeSetEpoch)) {
            setFullLoginLink(loginLinkLocalStorageDetails?.loginLink);
        } else {
            console.warn('login link expired');
        }
    };

    const removeFullLoginLinkFromLocalStorage = () => {
        localStorage.removeItem(loginLinkLocalStorageKey);
    };

    // ---------------
    // Subscriber cobranding
    // ---------------
    const getCoBranding = async () => {
        setCoBrandingChecked(true);
        if (subscriberId) {
            const [siteInfoResponse, loginInfoResponse] = await Promise.allSettled([
                API.get.siteInfo(restAPIBaseUrl, `?${EQueryStringParam.studioId}=${subscriberId}`, subscriberId),
                API.get.loginInfo(`?${EQueryStringParam.subscriberId}=${subscriberId}`),
            ]);

            let siteInfoData: TSiteInfoResponse;
            let loginInfoData: TLoginInfoResponse;
            if (siteInfoResponse.status === EPromiseResolution.fulfilled && siteInfoResponse.value.status === 200) {
                siteInfoData = siteInfoResponse.value.data;
            } else {
                console.error('Unable to retrieve site info');
            }
            //don't log error for 204s, simply skip populating siteInfoData
            if (loginInfoResponse.status === EPromiseResolution.fulfilled && loginInfoResponse.value.status === 200) {
                loginInfoData = loginInfoResponse.value.data;
            } else if (loginInfoResponse.status === EPromiseResolution.rejected) {
                console.error('Unable to retrieve login info');
            }

            setCoBranding({
                ...defaultCoBranding,
                ...(siteInfoData && {
                    name: siteInfoData.StudioName,
                    url: siteInfoData.BusinessUrl,
                    logoUrl: siteInfoData.LogoUrl,
                }),
                ...(loginInfoData && {
                    brandColor: loginInfoData.primaryColor,
                    isDarkMode: loginInfoData.isDarkMode,
                    isDefaultFamilyAccount: loginInfoData.isDefaultFamilyAccount,
                }),
            });
        }
    };

    const setupCobranding = () => {
        if (coBranding && coBranding.logoUrl) {
            const logo: HTMLImageElement = new Image();
            logo.onerror = () => {
                setCoBranding({ ...coBranding, logoUrl: '' });
                console.error('Failed to load subscriber logo');
            };
            logo.src = coBranding.logoUrl;
        }
        if (coBranding.brandColor) {
            const { brandColor } = coBranding;
            const darkerBrandColor = colord(brandColor).darken(0.1).toHex();
            setTheme({
                ...theme,
                palette: {
                    ...theme.palette,
                    primary: { ...theme.palette.primary, main: brandColor, dark: darkerBrandColor },
                },
            });
        }
    };

    useEffect(() => {
        setupCobranding();
    }, [coBranding]);

    useEffect(() => {
        if (isStaffLogin) {
            setTheme(MindbodyBusiness);
        }
    }, [isStaffLogin]);

    // --------------
    // Get CSRF
    // --------------
    const getCsrfToken = async () => {
        try {
            const response = await API.get.csrfToken();
            setCsrfToken(response.data.requestVerificationToken);
        } catch {
            setAppUiState(EAppUiState.error);
            console.error('Failed to get csrf token');
        }
    };

    // ---------------
    // Launch Darkly
    // ---------------
    const updateLaunchDarklyContext = async (userId: string) => {
        const currentLdContext = ldClient.getContext() as LDSingleKindContext;
        const newLdContext: LDSingleKindContext = {
            ...currentLdContext,
            key: userId,
        };
        await ldClient.identify(newLdContext);
    };

    // ----------------
    // Mixpanel
    // -----------------
    const setupMixpanel = () => {
        mixpanel.init(mixPanelEnvId, {
            ...(mixPanelEnvId === EMixpanelEnvId.dev && { debug: true }),
            persistence: 'localStorage',
        });
        if (!mixpanelEntryPointWhitelist.includes(entryPointUrl.path)) {
            mixpanel.reset();
        }
        mixpanel.register({
            app: EMixpanelAppName.identity,
        });
        setIsMixpanelSetup(true);
    };

    useEffect(() => {
        if (isMixpanelSetup) {
            mixpanel.register({
                userType: isStaffLogin ? EUserType.staff : EUserType.consumer,
                isDefaultFamilyAccount: coBranding.isDefaultFamilyAccount,
                isNewConsumerIdentityEnabled,
                isPasscodeVerificationEnabled,
                ...(mixpanelSurface && { surface: mixpanelSurface }),
            });
        }
    }, [
        isStaffLogin,
        isMixpanelSetup,
        isNewConsumerIdentityEnabled,
        isPasscodeVerificationEnabled,
        coBranding,
        mixpanelSurface,
    ]);

    // -------------
    // Ngrok
    // ------------
    const parseEnvironment = (environment: string): string => {
        if (environment.includes(EAppEnvironment.ngrok)) {
            return EAppEnvironment.ngrok;
        }
        return environment;
    };

    // --------------
    // Environment setup
    // ---------------
    const setupEnvironments = () => {
        let gatewayAPIBaseUrl: EGatewayAPIBaseUrl;
        let restAPIBaseUrl: ERestAPIBaseUrl;
        let connectBaseUrl: EConnectBaseUrl;
        let webClientsBaseUrl: EWebClientsBaseUrl;
        let adminBaseUrl: EAdminBaseUrl;
        let mixPanelEnvId: EMixpanelEnvId;
        const environment = parseEnvironment(window.location.origin);
        switch (environment) {
            case EAppEnvironment.devLocal:
                gatewayAPIBaseUrl = EGatewayAPIBaseUrl.devSandbox;
                restAPIBaseUrl = ERestAPIBaseUrl.dev;
                connectBaseUrl = EConnectBaseUrl.dev;
                webClientsBaseUrl = EWebClientsBaseUrl.dev;
                adminBaseUrl = EAdminBaseUrl.dev;
                mixPanelEnvId = EMixpanelEnvId.dev;
                break;
            case EAppEnvironment.local:
                gatewayAPIBaseUrl = EGatewayAPIBaseUrl.local;
                restAPIBaseUrl = ERestAPIBaseUrl.dev;
                connectBaseUrl = EConnectBaseUrl.dev;
                webClientsBaseUrl = EWebClientsBaseUrl.dev;
                adminBaseUrl = EAdminBaseUrl.dev;
                mixPanelEnvId = EMixpanelEnvId.dev;
                break;
            case EAppEnvironment.ngrok:
                gatewayAPIBaseUrl = EGatewayAPIBaseUrl.local;
                restAPIBaseUrl = ERestAPIBaseUrl.dev;
                connectBaseUrl = EConnectBaseUrl.dev;
                webClientsBaseUrl = EWebClientsBaseUrl.dev;
                adminBaseUrl = EAdminBaseUrl.dev;
                mixPanelEnvId = EMixpanelEnvId.dev;
                break;
            case EAppEnvironment.staging:
                gatewayAPIBaseUrl = EGatewayAPIBaseUrl.staging;
                restAPIBaseUrl = ERestAPIBaseUrl.production;
                connectBaseUrl = EConnectBaseUrl.production;
                webClientsBaseUrl = EWebClientsBaseUrl.production;
                adminBaseUrl = EAdminBaseUrl.production;
                mixPanelEnvId = EMixpanelEnvId.dev;
                break;
            case EAppEnvironment.devSandbox:
                gatewayAPIBaseUrl = EGatewayAPIBaseUrl.devSandbox;
                restAPIBaseUrl = ERestAPIBaseUrl.dev;
                connectBaseUrl = EConnectBaseUrl.dev;
                webClientsBaseUrl = EWebClientsBaseUrl.dev;
                adminBaseUrl = EAdminBaseUrl.dev;
                mixPanelEnvId = EMixpanelEnvId.dev;
                break;
            case EAppEnvironment.production:
                gatewayAPIBaseUrl = EGatewayAPIBaseUrl.production;
                restAPIBaseUrl = ERestAPIBaseUrl.production;
                connectBaseUrl = EConnectBaseUrl.production;
                webClientsBaseUrl = EWebClientsBaseUrl.production;
                adminBaseUrl = EAdminBaseUrl.production;
                mixPanelEnvId = EMixpanelEnvId.production;
                break;
            default:
                gatewayAPIBaseUrl = EGatewayAPIBaseUrl.production;
                restAPIBaseUrl = ERestAPIBaseUrl.production;
                connectBaseUrl = EConnectBaseUrl.production;
                webClientsBaseUrl = EWebClientsBaseUrl.production;
                adminBaseUrl = EAdminBaseUrl.production;
                mixPanelEnvId = EMixpanelEnvId.production;
                break;
        }
        setGatewayAPIBaseUrl(gatewayAPIBaseUrl);
        setRestAPIBaseUrl(restAPIBaseUrl);
        setConnectBaseUrl(connectBaseUrl);
        setWebClientsBaseUrl(webClientsBaseUrl);
        setAdminBaseUrl(adminBaseUrl);
        setMixpanelEnvId(mixPanelEnvId);
    };

    // ----------
    // App Setup
    // ----------
    const setupInitialData = () => {
        setupEnvironments();
        const pathname = window.location.pathname as ERoutes;
        setEntryPointUrl({ path: pathname, isAtEntryPoint: true });
        setAppUiState(EAppUiState.initialized);
    };

    const postInitializedSetup = async () => {
        setupMixpanel();
        setAppUiState(EAppUiState.ready);
    };

    useEffect(() => {
        switch (appUiState) {
            case EAppUiState.default:
                setupInitialData();
                break;
            case EAppUiState.initialized:
                postInitializedSetup();
                break;
            default:
                break;
        }
    }, [appUiState]);

    useEffect(() => {
        if (entryPointUrl?.path !== location?.pathname && entryPointUrl?.isAtEntryPoint) {
            setEntryPointUrl({ ...entryPointUrl, isAtEntryPoint: false });
        }
    }, [location]);

    return (
        <GlobalContext.Provider
            value={{
                language,
                browser,
                staffAccountType,
                setStaffAccountType,
                csrfToken,
                putFullLoginLinkInLocalStorage,
                getFullLoginLinkFromLocalStorage,
                removeFullLoginLinkFromLocalStorage,
                getCsrfToken,
                countriesList,
                getCountriesList,
                gatewayAPIBaseUrl,
                restAPIBaseUrl,
                connectBaseUrl,
                webClientsBaseUrl,
                adminBaseUrl,
                identityClientId,
                setIdentityClientId,
                isStaffLogin,
                setIsStaffLogin,
                subscriberId,
                setSubscriberId,
                customerAccountId,
                setCustomerAccountId,
                getCoBranding,
                coBranding,
                setCoBranding,
                coBrandingChecked,
                returnUrlParameter,
                setReturnUrlParameter,
                fullLoginLink,
                setFullLoginLink,
                consumerLoginLink,
                setConsumerLoginLink,
                staffLoginLink,
                setStaffLoginLink,
                appUiState,
                setAppUiState,
                updateLaunchDarklyContext,
                setMixpanelSurface,
                entryPointUrl,
            }}
            {...props}
        >
            <ThemeProvider theme={theme}>
                {appUiState === EAppUiState.ready && props.children}
                {appUiState === EAppUiState.error && (
                    <>
                        <Redirect to={ERoutes.error} />
                        {props.children}
                    </>
                )}
            </ThemeProvider>
        </GlobalContext.Provider>
    );
};

export default GlobalContext;
